import { ArrowDownIcon } from '@/components/icons'
import useTranslation from '@/hooks/useTranslation'
import { SxProps, Tooltip } from '@mui/material'
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { Box, Stack } from '@mui/system'
import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useMenuContext } from './MenuContext'

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  backgroundColor: 'transparent',
  border: 'none ',
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    padding: '10px 0',

    lineHeight: '1.3',
    fontSize: '12px',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.8)',
    minHeight: 'auto',
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: 'white',
  },
}))

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowDownIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  padding: 0,
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(180deg)',
  },
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  borderTop: 'none',
  padding: '0 0 20px',
  color: 'white',
  fontSize: '13px',
}))

interface MenuAccordionItemProps {
  icon?: any
  text?: string
  to?: string | any
  sx?: SxProps
  disabled?: boolean
  closeMenu: () => void
  menuCollapse?: boolean
}

export const MenuAccordionItem = ({
                                    icon,
                                    text,
                                    to = '',
                                    sx,
                                    disabled,
                                    closeMenu,
                                    menuCollapse = false,
                                  }: MenuAccordionItemProps) => {
  const location = useLocation()
  const [tooltipOpen, setTooltipOpen] = useState(false)

  const { t } = useTranslation()

  const handleTooltipOpen = () => {
    setTooltipOpen(true)
  }

  const handleTooltipClose = () => {
    setTooltipOpen(false)
  }
  useEffect(() => {
    if (location.pathname) {
      closeMenu()
    }
  }, [location.pathname, closeMenu])

  if (disabled) {
    return (
      <Stack
        gap="12px"
        direction="row"
        alignItems="center"
        sx={{
          padding: menuCollapse ? '16px 26px' : '10px',
          borderRadius: '5px',
          color: 'white',
          opacity: 0.4,
          userSelect: 'none',
          cursor: 'not-allowed',
          ...sx,
        }}
      >
        <Stack className="menu-accordion__item__icon">{icon}</Stack>
        {!menuCollapse && (
          <Box
            className="menu-accordion__item__text"
            sx={{
              fontSize: '13px',
              fontWeight: 300,
            }}
          >
            {t(text + '')}
          </Box>
        )}
      </Stack>
    )
  }

  return (
    <NavLink
      end
      to={to}
      style={{
        userSelect: 'none',
        textDecoration: 'none',
      }}
    >
      <Tooltip
        placement="right"
        title={text || ''}
        open={tooltipOpen && menuCollapse}
        onOpen={handleTooltipOpen}
        onClose={handleTooltipClose}
        arrow
      >
        <Stack
          gap="12px"
          direction="row"
          alignItems="center"
          sx={{
            padding: menuCollapse ? '16px 26px' : '10px',
            borderRadius: '5px',
            color: 'white',
            '.active &': {
              color: 'pink',
            },
            '&:hover, .active>&': {
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
            },
            '&:hover .menu-accordion__item__icon, &:hover .menu-accordion__item__text, .active>& .menu-accordion__item__icon, .active>& .menu-accordion__item__text  ':
              {
                color: '#66F0A0',
              },

            ...sx,
          }}
        >
          <Stack className="menu-accordion__item__icon" color="#8A8A8A">
            {icon}
          </Stack>
          {!menuCollapse && (
            <Box
              className="menu-accordion__item__text"
              sx={{
                fontSize: '13px',
                fontWeight: 300,
              }}
            >
              {t(text + '')}
            </Box>
          )}
        </Stack>
      </Tooltip>
    </NavLink>
  )
}

interface MenuAccordion {
  id?: string
  title?: string
  children: any
  sx?: SxProps
  alwaysExpanded?: boolean
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
export default function MenuAccordion({
                                        id,
                                        title,
                                        children,
                                        sx,
                                        alwaysExpanded = false,
                                      }: MenuAccordion) {
  const { t } = useTranslation()

  const [expanded, setExpanded] = useState<string>()


  useEffect(() => {
    if (alwaysExpanded) return
    if (location.pathname.split('/')[1] === 'dashboard') {
      setExpanded('left-menu-main-menu')
    }
    if (location.pathname.split('/')[1] === 'config') {
      setExpanded('left-menu-data-config')
    }
    if (location.pathname.split('/')[2] === 'station-config') {
      setExpanded('left-menu-station-config')
    }
    if (
      location.pathname.split('/')[2] === 'sensors' ||
      location.pathname === '/config/sensors/etz'
    ) {
      setExpanded('left-menu-sensors')
    }

    if (location.pathname.split('/')[2] === 'other') {
      setExpanded('left-menu-other')
    }
  }, [location, alwaysExpanded])

  const handleAccordClick = (id: any) => {
    if (alwaysExpanded) return
    if (id === expanded) {
      setExpanded('')
    }
    if (id !== expanded) {
      setExpanded(id)
    }
  }

  return (
    <Accordion
      expanded={alwaysExpanded || id === expanded}
      onChange={() => handleAccordClick(id)}
      sx={sx}
    >
      <AccordionSummary
        aria-controls={id}
        id={id}
        sx={
          alwaysExpanded
            ? {
              '& .MuiAccordionSummary-expandIconWrapper': {
                display: 'none',
              },
              cursor: 'default !important',
            }
            : undefined
        }
      >
        <Typography color="#FFF">{t(title + '')}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap="4px">{children}</Stack>
      </AccordionDetails>
    </Accordion>
  )
}
